import {useState} from 'react';
import {I18n} from 'aws-amplify/utils';
import {
    Button,
    Group,
    Stack,
} from '@mantine/core';

import {RecordFormComponent} from './RecordFormComponent';
import {
    RecordFormValues,
    validateRecordForm,
} from './recordFormValues';
import {DnsRecordChangeInput} from '../types';

// FNP-1192 — the record form.
//
// ⚠️ VALIDATION RUNS BEFORE SUBMIT AND BLOCKS IT. That is the acceptance
// criterion: a malformed value is caught client-side, next to the field, rather
// than coming back as a provider rejection in a toast after the drawer has
// closed and taken the operator's typing with it.
//
// ⚠️ NOT Formik, unlike the zones FILTER form. The zones filter has two
// independent Select fields and no cross-field rules; this form's validation is
// entirely cross-field — every value is checked AGAINST THE CHOSEN TYPE, and a
// dynamic list of values needs per-row errors keyed by index. Expressing that
// through Formik's field-name machinery is more indirection than the rules
// themselves, so the state is plain and the rules are a pure function that the
// tests call directly.

export const RecordForm = (
    {
        initialValues,
        supportedRecordTypes,
        supportedTypesAreProviderReported,
        isEdit,
        isSubmitting,
        onSubmit,
        onCancel,
    }: {
        initialValues: RecordFormValues,
        supportedRecordTypes: string[],
        supportedTypesAreProviderReported: boolean,
        isEdit: boolean,
        isSubmitting: boolean,
        onSubmit: (record: DnsRecordChangeInput, values: RecordFormValues) => Promise<void>,
        onCancel: () => void,
    },
) => {

    const [values, setValues] = useState<RecordFormValues>(initialValues);
    // Errors appear only after a submit attempt. Marking a half-typed IP
    // address as invalid on the first keystroke is noise, not help.
    const [errors, setErrors] = useState<Record<string, string>>({});

    const handleChange = (next: Partial<RecordFormValues>) => {
        setValues(current => ({
            ...current,
            ...next,
        }));
    };

    const handleSubmit = async () => {

        const found = validateRecordForm(values);
        setErrors(found);

        if (Object.keys(found).length > 0) {
            return;
        }

        // Blank rows are dropped rather than sent: an operator who added a
        // value box and did not fill it meant to add nothing, and an empty
        // string is a value the provider would reject.
        const cleanedValues = values.values
                                    .map(value => value.trim())
                                    .filter(value => value !== '');

        // ⚠️ AWAITED, AND A FAILURE PROPAGATES. The caller closes the drawer
        // only when this resolves, so a rejected write leaves the drawer open
        // with the operator's input intact rather than discarding it and
        // leaving only a toast to explain a form they can no longer correct.
        await onSubmit(
            {
                action: 'UPSERT',
                name: values.name.trim(),
                type: values.type,
                ttlSeconds: values.ttlSeconds,
                values: cleanedValues,
            },
            values,
        );
    };

    return (
        <Stack gap={'lg'}>

            <RecordFormComponent
                values={values}
                errors={errors}
                supportedRecordTypes={supportedRecordTypes}
                supportedTypesAreProviderReported={supportedTypesAreProviderReported}
                isEdit={isEdit}
                onChange={handleChange}
            />

            <Group justify={'flex-end'}>
                <Button variant={'default'} onClick={onCancel} disabled={isSubmitting}>
                    {I18n.get('Cancel')}
                </Button>
                <Button onClick={handleSubmit} loading={isSubmitting}>
                    {isEdit ? I18n.get('Save record') : I18n.get('Create record')}
                </Button>
            </Group>

        </Stack>
    );
};
